/**
 * Preserves technical frontend errors in the console while presenting one
 * localized, nontechnical toast. Repeated failures are deduplicated visually,
 * never swallowed, and remain observable by global error handlers.
 */
import { showNotification, toastLabels } from './notifications'
import { Log } from './logger'
import type { ToastDetails } from './toast-details'

type FrontendErrorState = {
  listenersInstalled: boolean
  recentToasts: Map<string, number>
  reportedErrors: WeakSet<object>

}

declare global {
  interface Window {
    __dishBookErrorState?: FrontendErrorState
  }
}

const TOAST_LIFETIME_MS = 12_000
// Repeated retries should keep logging diagnostics without stacking identical
// notices in front of the user.
const DUPLICATE_WINDOW_MS = 30_000

// Each page can load several independently bundled entry points. Keeping the
// state on window prevents duplicate listeners and duplicate notices.
/**
 * Returns the page-wide error state shared by independently loaded browser bundles.
 */
function errorState(): FrontendErrorState {
  window.__dishBookErrorState ??= {
    listenersInstalled: false,
    recentToasts: new Map(),
    reportedErrors: new WeakSet(),
  }

  return window.__dishBookErrorState
}

/**
 * Converts any thrown JavaScript value into an Error without discarding the original cause.
 */
function normalizeError(error: unknown): Error {
  if (error instanceof Error) {
    return error
  }

  if (typeof error === 'string' && error.trim() !== '') {
    return new Error(error)
  }

  return new Error('Unknown frontend error', { cause: error })
}

/**
 * Returns the localized generic failure message configured on the page.
 */
function defaultUserMessage(): string {
  return document.body?.dataset.frontendErrorMessage ?? ''
}

/**
 * Displays one deduplicated localized error toast while preserving console diagnostics.
 */
function showErrorToast(message: string, signature: string, details?: ToastDetails): void {
  const state = errorState()
  const now = Date.now()
  state.recentToasts.forEach((shownAt, key) => {
    if (now - shownAt >= DUPLICATE_WINDOW_MS) {
      state.recentToasts.delete(key)
    }
  })
  const lastShown = state.recentToasts.get(signature) ?? 0

  if (now - lastShown < DUPLICATE_WINDOW_MS) {
    return
  }

  state.recentToasts.set(signature, now)

  showNotification(message, {
    kind: 'error',
    source: 'frontend',
    lifetimeMs: TOAST_LIFETIME_MS,
    dismissLabel: toastLabels().dismiss,
    details,
    detailsLabel: toastLabels().details,
  })
}

/**
 * Presents a localized validation failure without pretending the operation succeeded.
 */
export function showValidationError(message: string): void {
  showErrorToast(message, `validation:${message}`)
}

/**
 * Logs technical error details once and presents localized nontechnical feedback; the toast shows
 * only the user message and the bounded details, never the error's own message.
 */
export function reportFrontendError(
  error: unknown,
  userMessage = defaultUserMessage(),
  details?: ToastDetails,
): Error {
  const normalized = normalizeError(error)
  const state = errorState()

  if (!state.reportedErrors.has(normalized)) {
    state.reportedErrors.add(normalized)

    if (!Log.hasException(normalized) && !Log.operationActive()) {
      try {
        Log.notice('Report frontend action failure', () => {
          showErrorToast(
            userMessage,
            `${userMessage}\n${normalized.name}\n${normalized.message}`,
            details,
          )

          throw normalized
        })
      } catch (loggedError) {
        // The logger deliberately rethrows the same exception after preserving
        // it. The public reporting contract returns that exception so the
        // caller or global browser event retains control of propagation.
        if (loggedError !== normalized) {
          throw loggedError
        }
      }
    } else {
      showErrorToast(
        userMessage,
        `${userMessage}\n${normalized.name}\n${normalized.message}`,
        details,
      )
    }
  }

  return normalized
}

/**
 * Reports a frontend failure and rethrows it so global diagnostics remain observable.
 */
export function throwFrontendError(error: unknown, userMessage?: string): never {
  throw reportFrontendError(error, userMessage)
}

/**
 * Owns the `installGlobalErrorHandlers()` step of the errors browser workflow.
 */
function installGlobalErrorHandlers(): void {
  const state = errorState()

  if (state.listenersInstalled) {
    return
  }

  state.listenersInstalled = true
  // Errors already reported by an action are ignored here through reportedErrors.
  window.addEventListener('error', (event) => {
    reportFrontendError(event.error ?? new Error(event.message || 'Unhandled frontend error'))
  })
  window.addEventListener('unhandledrejection', (event) => {
    reportFrontendError(event.reason)
  })
}

installGlobalErrorHandlers()
